import React, { useEffect, useRef, useState } from "react";
import ProductImageGallery from "../../../components/customer/ProductDetailComponent.tsx/ProductImageGallery";
import { JoseraFinal } from "../../../utils/Customer/products";
import CommonButton from "../../../components/CommonButton";
import CartButton from "../../../components/customer/CartButton";
import FrequentlyBought from "../../../components/customer/ProductDetailComponent.tsx/FrequentlyBought";
import ReviewSection from "../../../components/customer/ProductDetailComponent.tsx/ReviewSection";
import { ShieldCheck, Star } from "lucide-react";
import SizeSelector from "../../../components/customer/SizeSelector";
import YouMayLikeProductSection from "../../../components/customer/ProductDetailComponent.tsx/YouMayLikeProductSection";
import CartDrawer, {
  CartItem,
  CartBundle,
} from "../../../components/customer/ProductDetailComponent.tsx/CartDrawer";
import CountdownTimer from "../../../components/customer/ProductDetailComponent.tsx/CountdownTimer";
import AccordionItem from "../../../components/customer/ProductDetailComponent.tsx/AccordionComp";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import SubscribeAndSave from "../../../components/customer/ProductDetailComponent.tsx/SubscribeAndSave";

import { useLazyGetProductDetailsQuery } from "../../../redux/actions/Customer/serviceCustomer";
import {
  useCreateCartMutation,
  useLazyGetCartQuery,
  useUpdateCartMutation,
  useLazyDeleteCartQuery,
  useAddBundleToCartMutation,
} from "../../../redux/actions/Customer/cartCustomer";
import { Productsections } from "../../../utils/Customer/CustomerData";
import {
  BundleProduct,
  RelatedProduct,
} from "../../../types/Response/CustomerResponse";
import QuantityStepper from "../../../components/customer/ProductDetailComponent.tsx/QuantityStepper";
import { useAlert } from "../../../components/customer/AlertContext";
import { AddBundleToCartRequest } from "../../../types/Request/CustomerRequest";
import { useSelector } from "react-redux";
import { RootState } from "../../../redux/rootReducer";
import { useAnalytics } from "../../../utils/Customer/useAnalytics";

interface IAttributeTagCategory {
  category: string;
  values: string[];
}

const CustomerNewProductDetailsPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { id } = useParams();
  const { showConfirm } = useAlert();

  const [qty, setQty] = useState(1);
  const [cart, setCart] = useState<CartItem[]>([]);
  const [cartBundles, setCartBundles] = useState<CartBundle[]>([]);
  const [isOpen, setIsOpen] = useState(false);
  const [openIndex, setOpenIndex] = useState<number | null>(null);
  const [purchaseType, setPurchaseType] = useState<"subscribe" | "one-time">(
    "one-time",
  );
  const [selectedBillingCycle, setSelectedBillingCycle] = useState<number>(2);
  const [selectedInterval, setSelectedInterval] = useState<number>(1);
  const [showStickyBar, setShowStickyBar] = useState(false);
  const lastScrollY = useRef(0);
  const productInfoRef = useRef<HTMLDivElement | null>(null);
  const [isMobileLayout, setIsMobileLayout] = useState(window.innerWidth < 768);

  // RTK Query setups
  const [triggerGetProductDetails, { data: apiResponse, isLoading }] =
    useLazyGetProductDetailsQuery();
  const [createCart] = useCreateCartMutation();
  const [triggerGetCart] = useLazyGetCartQuery();
  const [updateCart] = useUpdateCartMutation();
  const [triggerDeleteCart] = useLazyDeleteCartQuery();
  const [AddBundleToCart] = useAddBundleToCartMutation();
  const isLoggedIn = useSelector(
    (state: RootState) => state.auth.customer.isLoggedIn,
  );

  const { trackProductView } = useAnalytics();
  const trackedProductIdRef = useRef<number | null>(null);

  useEffect(() => {
    const handleResize = () => setIsMobileLayout(window.innerWidth < 768);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    if (id) {
      triggerGetProductDetails(Number(id));
      setQty(1);
      setPurchaseType("one-time");
    }
  }, [id, triggerGetProductDetails]);

  useEffect(() => {
    const handleScroll = () => {
      const currentScrollY = window.scrollY;
      if (currentScrollY > lastScrollY.current && currentScrollY > 200) {
        setShowStickyBar(true);
      } else {
        setShowStickyBar(false);
      }
      lastScrollY.current = currentScrollY;
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const scrollToProductInfo = () => {
    if (productInfoRef.current) {
      const yOffset = -80;
      const y =
        productInfoRef.current.getBoundingClientRect().top +
        window.scrollY +
        yOffset;
      window.scrollTo({ top: y, behavior: "smooth" });
    }
  };

  const fetchedProduct = apiResponse?.data;
  const staticProduct =
    location.state?.product ||
    JoseraFinal.find((item) => item.productid === id);

  const productName = fetchedProduct?.name || staticProduct?.productname || "";
  const productId =
    fetchedProduct?.productId?.toString() || staticProduct?.productid || "";
  const productDescription =
    fetchedProduct?.description || staticProduct?.description || "";

  // Multi-Variant Engine Resolution
  const hasFamily = fetchedProduct?.productFamily?.hasFamily;
  const familyProducts = fetchedProduct?.productFamily?.products || [];
  const currentFamilyItem = familyProducts.find((p) => p.isCurrentProduct);

  const shortDescription =
    currentFamilyItem?.shortDescription ||
    fetchedProduct?.shortDescription ||
    "";

  const familySpecs = currentFamilyItem?.specifications || [];

  // Parse manufacture date for Item Details display
  const mfgSpec = familySpecs.find(
    (s: any) => s.section === "Manufacture" || s.key === "Manufacture Date",
  );
  const manufactureDateValue = mfgSpec?.value || "";

  const ingredientsSpec = familySpecs.find(
    (s: any) => s.section === "Ingredients" || s.key === "Product Ingredients",
  );
  const instructionsSpec = familySpecs.find(
    (s: any) => s.section === "Instructions" || s.key === "Usage Instructions",
  );
  const marketingSpecs = familySpecs.filter(
    (s: any) => s.section === "Marketing" || s.key?.includes("Marketing Text"),
  );

  const stockCount = currentFamilyItem?.stock ?? 0;

  const uniqueColors = Array.from(
    new Set(familyProducts.map((p) => p.color).filter(Boolean)),
  );
  const uniqueSizes = Array.from(
    new Set(familyProducts.map((p) => p.size).filter(Boolean)),
  );
  const uniqueWeights = Array.from(
    new Set(
      familyProducts
        .map((p) =>
          p.weight !== null && Number(p.weight) > 0
            ? `${p.weight} ${p.weightUnit || "kg"}`
            : "",
        )
        .filter(Boolean),
    ),
  );

  const activeColor =
    currentFamilyItem?.color && currentFamilyItem.color.trim() !== ""
      ? currentFamilyItem.color
      : undefined;
  const activeSize =
    currentFamilyItem?.size && currentFamilyItem.size.trim() !== ""
      ? currentFamilyItem.size
      : undefined;
  const activeWeightString =
    currentFamilyItem?.weight !== null && Number(currentFamilyItem?.weight) > 0
      ? `${currentFamilyItem?.weight} ${currentFamilyItem?.weightUnit || "kg"}`
      : undefined;

  const handleVariantSwitch = (
    type: "color" | "size" | "weight",
    chosenValue: string,
  ) => {
    let match: any = null;

    if (type === "size") {
      match =
        familyProducts.find(
          (p) => p.size === chosenValue && p.color === (activeColor || ""),
        ) || familyProducts.find((p) => p.size === chosenValue);
    } else if (type === "color") {
      match =
        familyProducts.find(
          (p) => p.color === chosenValue && p.size === (activeSize || ""),
        ) || familyProducts.find((p) => p.color === chosenValue);
    } else if (type === "weight") {
      match = familyProducts.find((p) => {
        const wString =
          p.weight !== null ? `${p.weight} ${p.weightUnit || "kg"}` : "";
        return wString === chosenValue;
      });
    }

    if (!match) {
      match = familyProducts.find((p) => p.productId === Number(productId));
    }

    if (match) {
      navigate(`/productpage/customerneproductpage/${match.productId}`);
    }
  };

  // Pricing Calculation
  let displayPrice = "";
  let salePrice: string | undefined = undefined;
  let inStockStatus = (fetchedProduct?.inStock ?? true) && stockCount > 0;

  if (fetchedProduct) {
    if (currentFamilyItem) {
      displayPrice = `AED ${currentFamilyItem.price}`;
      if (currentFamilyItem.salePrice && currentFamilyItem.salePrice > 0) {
        salePrice = `AED ${currentFamilyItem.salePrice}`;
      }
      inStockStatus = currentFamilyItem.isInStock && stockCount > 0;
    } else {
      displayPrice = `AED ${fetchedProduct.variants?.[0]?.price || 0}`;
      if (
        fetchedProduct.variants?.[0]?.salePrice &&
        fetchedProduct.variants?.[0]?.salePrice > 0
      ) {
        salePrice = `AED ${fetchedProduct.variants[0].salePrice}`;
      }
    }
  } else {
    displayPrice = staticProduct?.price || "AED 0";
    salePrice = staticProduct?.saleprice || undefined;
    inStockStatus = staticProduct?.inStock !== false;
  }

  const getRemainingSeconds = (
    startDate?: string | null,
    endDate?: string | null,
  ) => {
    if (!startDate || !endDate) return 0;
    const today = new Date();
    const startTime = new Date(startDate).getTime();
    const endTime = new Date(endDate).getTime();
    const now = today.getTime();

    if (now >= startTime && now < endTime) {
      return Math.max(0, Math.floor((endTime - now) / 1000));
    }
    return 0;
  };

  const [remainingSeconds, setRemainingSeconds] = useState(0);

  useEffect(() => {
    if (currentFamilyItem?.saleStartDate && currentFamilyItem?.saleEndDate) {
      setRemainingSeconds(
        getRemainingSeconds(
          currentFamilyItem.saleStartDate,
          currentFamilyItem.saleEndDate,
        ),
      );
    } else if (staticProduct?.saleStartDate && staticProduct?.saleEndDate) {
      setRemainingSeconds(
        getRemainingSeconds(
          staticProduct.saleStartDate,
          staticProduct.saleEndDate,
        ),
      );
    } else {
      setRemainingSeconds(0);
    }
  }, [fetchedProduct, currentFamilyItem, staticProduct]);

  useEffect(() => {
    if (remainingSeconds <= 0) return;
    const interval = setInterval(() => {
      setRemainingSeconds((prev) => (prev > 0 ? prev - 1 : 0));
    }, 1000);
    return () => clearInterval(interval);
  }, [remainingSeconds]);

  const originalNumeric = parseFloat(
    displayPrice?.replace(/[^0-9.]/g, "") || "0",
  );
  const activeNumeric = parseFloat(
    (salePrice || displayPrice)?.replace(/[^0-9.]/g, "") || "0",
  );
  const discount =
    originalNumeric > 0 && salePrice
      ? Math.round(((originalNumeric - activeNumeric) / originalNumeric) * 100)
      : 0;

  const normalizedFeatures = fetchedProduct?.features
    ? fetchedProduct.features.map((f, i) => ({ id: i, title: f.feature }))
    : staticProduct?.keyFeatures || [];

  // =========================================================================
  // DYNAMIC ACCORDION & SPECIFICATIONS BUILDER
  // =========================================================================
  const buildDynamicAccordionSections = () => {
    const sections: any[] = [];

    // ---------------------------------------------------------
    // PRODUCT DESCRIPTION
    // ---------------------------------------------------------
    if (productDescription?.trim()) {
      sections.push({
        key: "description",
        title: "Product Description",
        type: "text",
        data: productDescription,
      });
    }

    // ---------------------------------------------------------
    // ITEM DETAILS
    // ---------------------------------------------------------
    const dynamicDetailsRows: {
      label: string;
      value: string;
    }[] = [];

    // First: current family product attributes
    if (
      currentFamilyItem?.attributes &&
      typeof currentFamilyItem.attributes === "object"
    ) {
      Object.entries(currentFamilyItem.attributes).forEach(([key, value]) => {
        if (
          value !== null &&
          value !== undefined &&
          String(value).trim() !== ""
        ) {
          dynamicDetailsRows.push({
            label: key,
            value: String(value),
          });
        }
      });
    }

    // Second: root product attributes
    const productAttributes = fetchedProduct?.attributes;

    if (dynamicDetailsRows.length === 0 && Array.isArray(productAttributes)) {
      productAttributes.forEach((attr: any) => {
        if (
          attr?.name &&
          attr?.value !== null &&
          attr?.value !== undefined &&
          String(attr.value).trim() !== ""
        ) {
          dynamicDetailsRows.push({
            label: attr.name,
            value: String(attr.value),
          });
        }
      });
    }

    if (dynamicDetailsRows.length > 0) {
      sections.push({
        key: "details",
        title: "Item Details",
        type: "table",
        data: dynamicDetailsRows,
      });
    }

    // ---------------------------------------------------------
    // MEASUREMENTS
    // ---------------------------------------------------------
    const dynamicMeasurementRows: {
      label: string;
      value: string;
    }[] = [];

    const targetVariant = currentFamilyItem || fetchedProduct?.variants?.[0];

    if (targetVariant) {
      if (
        targetVariant.weight !== null &&
        targetVariant.weight !== undefined &&
        Number(targetVariant.weight) > 0
      ) {
        dynamicMeasurementRows.push({
          label: "Item Weight",
          value: `${targetVariant.weight} ${targetVariant.weightUnit || "kg"}`,
        });
      }

      const hasDimensions =
        Number(targetVariant.length) > 0 ||
        Number(targetVariant.width) > 0 ||
        Number(targetVariant.height) > 0;

      if (hasDimensions) {
        dynamicMeasurementRows.push({
          label: "Item Dimensions (L × W × H)",
          value: `${targetVariant.length || 0}L × ${
            targetVariant.width || 0
          }W × ${targetVariant.height || 0}H cm`,
        });
      }
    }

    if (dynamicMeasurementRows.length > 0) {
      sections.push({
        key: "measurements",
        title: "Measurements",
        type: "table",
        data: dynamicMeasurementRows,
      });
    }

    return sections;
  };

  const dynamicSections = buildDynamicAccordionSections();

  const getCategorizedAttributeTags = (): IAttributeTagCategory[] => {
    const rawData =
      currentFamilyItem?.attributeTags ?? fetchedProduct?.attributeTags;

    if (!rawData) return [];

    if (Array.isArray(rawData)) {
      return rawData
        .map((item: any) => {
          if (typeof item === "string") {
            return { category: "General", values: [item] };
          }
          if (item?.category && Array.isArray(item?.values)) {
            return { category: item.category, values: item.values };
          }
          return null;
        })
        .filter(Boolean) as IAttributeTagCategory[];
    }

    if (typeof rawData === "object") {
      return Object.entries(rawData).map(([category, values]) => {
        const valArr = Array.isArray(values)
          ? values.map(String)
          : [String(values)];
        return { category, values: valArr };
      });
    }

    return [];
  };

  const categorizedTags = getCategorizedAttributeTags();
const syncCartWithServer = (serverItems: any[]) => {
    const mappedItems: CartItem[] = serverItems.map((item) => ({
        id: item.cartItemId.toString(),

        productId: item.productId,

        title: item.productName,

        price: item.isSubscription
            ? `AED ${Number(
                  item.subscriptionPrice ??
                  item.total ??
                  item.unitPrice ??
                  0
              ).toFixed(4)}`
            : `AED ${Number(
                  item.unitSalePrice ||
                  item.unitPrice ||
                  0
              ).toFixed(2)}`,

        image: item.imageUrl,

        quantity: item.quantity,

        isMandatory: item.isMandatory,

        // Variant
        variantName: item.variantName,

        // Subscription
        isSubscription: item.isSubscription ?? false,

        billingCycle:
            item.billingCycle ?? null,

        interval:
            item.interval ?? null,

        subscriptionDiscountPercent:
            item.subscriptionDiscountPercent ?? 0,

        subscriptionDiscountAmount:
            item.subscriptionDiscountAmount ?? 0,

        subscriptionPrice:
            item.subscriptionPrice ?? 0,

        recurringPrice:
            item.recurringPrice ?? 0,

        recurringDiscountPercent:
            item.recurringDiscountPercent ?? 0,

        // Original pricing
        unitPrice:
            item.unitPrice ?? 0,

        unitSalePrice:
            item.unitSalePrice ?? 0,
    }));

    setCart(mappedItems);
};

  const fetchLatestCart = async () => {
    try {
      const res = await triggerGetCart().unwrap();

      if (!res?.success || !res?.data) {
        return;
      }

      // ------------------------------------------------
      // Sync normal cart items
      // ------------------------------------------------

      syncCartWithServer(res.data.items ?? []);

      // ------------------------------------------------
      // Sync bundle cart items
      // ------------------------------------------------

      setCartBundles(res.data.bundles ?? []);
    } catch (error) {
      console.error("Failed to fetch latest cart:", error);
    }
  };

  const handleAdd = async () => {
    try {
      const isSub = purchaseType === "subscribe";

      const cartPayload = {
        productId: Number(productId),
        quantity: qty,
        isSubscription: isSub,
        billingCycle: isSub ? selectedBillingCycle : undefined,
        interval: isSub ? selectedInterval : undefined,
      };

      await createCart(cartPayload).unwrap();
      await fetchLatestCart();
      setIsOpen(true);
    } catch (err: any) {
      console.error(err);
      const errorMessage =
        err?.data?.Message ||
        err?.data?.message ||
        "Could not increase quantity.";
      if (isLoggedIn) {
        showConfirm({
          title: "",
          message: errorMessage,
          type: "error",
          confirmText: "Ok",
        });
      }
    }
  };

  const handleIncrement = () => {
    const maxAllowedStock = stockCount > 0 ? stockCount : 10;
    setQty((prev) => (prev < maxAllowedStock ? prev + 1 : prev));
  };

  const handleDecrement = () => setQty((prev) => (prev > 1 ? prev - 1 : 1));

  const handleCartIncrement = async (itemId: string) => {
    const currentItem = cart.find((item) => item.id === itemId);
    if (!currentItem) return;
    try {
      await updateCart({
        cartItemId: Number(itemId),
        quantity: currentItem.quantity + 1,
      }).unwrap();
      await fetchLatestCart();
    } catch (err: any) {
      console.error(err);
      const errorMessage =
        err?.data?.Message ||
        err?.data?.message ||
        "Could not increase quantity.";
      showConfirm({
        title: "",
        message: errorMessage,
        type: "error",
        confirmText: "Ok",
      });
    }
  };

  const handleCartDecrement = async (itemId: string) => {
    const currentItem = cart.find((item) => item.id === itemId);
    if (!currentItem) return;
    try {
      if (currentItem.quantity <= 1) {
        await handleCartRemove(itemId);
      } else {
        await updateCart({
          cartItemId: Number(itemId),
          quantity: currentItem.quantity - 1,
        }).unwrap();
        await fetchLatestCart();
      }
    } catch (err: any) {
      console.error(err);
      const errorMessage =
        err?.data?.Message ||
        err?.data?.message ||
        "Could not increase quantity.";
      showConfirm({
        title: "",
        message: errorMessage,
        type: "error",
        confirmText: "Ok",
      });
    }
  };

  const handleCartRemove = async (itemId: string) => {
    try {
      await triggerDeleteCart(Number(itemId)).unwrap();
      await fetchLatestCart();
    } catch (err: any) {
      console.error(err);
      const errorMessage =
        err?.data?.Message ||
        err?.data?.message ||
        "Could not increase quantity.";
      showConfirm({
        title: "",
        message: errorMessage,
        type: "error",
        confirmText: "Ok",
      });
    }
  };

  const handleAddToCartFromSlider = async (sliderProduct: any) => {
    try {
      await createCart({
        productId: Number(sliderProduct.productId),
        quantity: 1,
        isSubscription: false,
      }).unwrap();
      await fetchLatestCart();
      setIsOpen(true);
    } catch (error) {
      console.error(error);
    }
  };

  const handleAddBundleToCart = async (bundleItems: BundleProduct) => {
    try {
      const request: AddBundleToCartRequest = {
        bundleId: bundleItems.bundleId,
      };
      await AddBundleToCart(request).unwrap();

      await fetchLatestCart();
      setIsOpen(true);
    } catch (error: any) {
      console.log(error, "error");
      if (isLoggedIn) {
        showConfirm({
          title: "Error",
          message: error?.data?.Message || "Failed to Add product",
          type: "error",
          confirmText: "Ok",
        });
      }
    }
  };

  useEffect(() => {
    if (!isLoggedIn) {
      return;
    }

    fetchLatestCart();
  }, [isLoggedIn]);

  const handleAccordionClick = (index: number) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  const handleCheckoutClick = async () => {
      try {
      const isSub = purchaseType === "subscribe";

      const cartPayload = {
        productId: Number(productId),
        quantity: qty,
        isSubscription: isSub,
        billingCycle: isSub ? selectedBillingCycle : undefined,
        interval: isSub ? selectedInterval : undefined,
      };

      await createCart(cartPayload).unwrap();
      await fetchLatestCart();
    navigate("/cartpage/customercartpage");
    } catch (err: any) {
      console.error(err);
      const errorMessage =
        err?.data?.Message ||
        err?.data?.message ||
        "Could not increase quantity.";
      if (isLoggedIn) {
        showConfirm({
          title: "",
          message: errorMessage,
          type: "error",
          confirmText: "Ok",
        });
      }
    }
   
  };

  const mainImage =
    fetchedProduct?.images?.[0] || staticProduct?.productimage || "";

  const normalizedGalleryProduct = {
    ...staticProduct,
    productimage: mainImage,
    // Pass the live API video URL (or current family variant video URL)
    videoUrl: currentFamilyItem?.videoUrl || "",
    subimage:
      fetchedProduct?.images && fetchedProduct.images.length > 0
        ? fetchedProduct.images.map((img: string) => ({ image: img }))
        : staticProduct?.subimage || [],
    video: staticProduct?.video || [],
  };

  const relatedProducts: RelatedProduct[] =
    fetchedProduct?.relatedProducts || [];

  useEffect(() => {
    const numericProductId = Number(productId);

    if (!numericProductId || numericProductId <= 0) {
      return;
    }

    if (trackedProductIdRef.current === numericProductId) {
      return;
    }

    trackedProductIdRef.current = numericProductId;

    trackProductView(numericProductId);
  }, [productId, trackProductView]);

  return (
    <div className="py-4 px-6 text-left max-w-7xl mx-auto">
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-[1.4fr_2.6fr] gap-4">
        {/* IMAGE GALLERY */}
        <div className="w-full md:sticky md:top-[80px] self-start relative overflow-visible">
          <div className="absolute top-2 right-2 z-20">
            {remainingSeconds > 0 && (
              <CountdownTimer
                totalSeconds={remainingSeconds}
                className="w-auto"
              />
            )}
          </div>
          <ProductImageGallery
            product={normalizedGalleryProduct}
            layout={isMobileLayout ? "bottom" : "left"}
          />
        </div>

        {/* DATA CONTAINER AXIS */}
        <div>
          <div className="grid grid-cols-1 lg:grid-cols-[2.0fr_1fr] gap-6 lg:gap-3 items-stretch px-2">
            <div className="flex flex-col gap-2 h-full">
              <div className="flex flex-col md:flex-row md:items-center md:justify-between">
                <div className="flex items-center gap-3">
                  <CommonButton
                    text="BEST SELLER"
                    className="bg-offerColor border-none"
                    rounded="full"
                    textContainerClassName="px-3 py-0"
                    textClassName="text-[10px] font-semibold"
                  />
                  <div className="flex items-center gap-1">
                    <Star
                      size={14}
                      className="text-yellow-400 fill-yellow-400"
                    />
                    {fetchedProduct && fetchedProduct.rating > 0 ? (
                      <>
                        <span className="text-sm font-medium text-gray-700">
                          {fetchedProduct.rating.toFixed(1)}
                        </span>
                        <span className="text-xs text-gray-500">
                          ({fetchedProduct.totalReviews} Reviews)
                        </span>
                      </>
                    ) : (
                      <span className="text-sm text-gray-500">
                        No Ratings Yet
                      </span>
                    )}
                  </div>
                </div>
              </div>

              {/* TITLE */}
              <h1 className="text-xl md:text-2xl font-bold text-logoHeading">
                {productName}
              </h1>

              {/* PRICE HIGHLIGHT */}
              <div className="flex items-center gap-2">
                <span className="text-lg md:text-xl font-bold text-mainBrandColor">
                  {salePrice || displayPrice}
                </span>
                {salePrice && (
                  <span className="line-through text-gray-400 text-sm">
                    {displayPrice}
                  </span>
                )}
                {discount > 0 && (
                  <span className="text-xs text-white bg-logoHeading px-1.5 py-[2px] rounded font-semibold whitespace-nowrap">
                    Save {discount}%
                  </span>
                )}
              </div>

              {/* DYNAMIC SIZES SELECTORS */}
              {hasFamily && familyProducts.length > 0 && (
                <div className="border-y border-slate-50 py-3">
                  {/* Colors Selector */}
                  {uniqueColors.length > 0 && (
                    <SizeSelector
                      sizes={uniqueColors}
                      selectedSize={activeColor}
                      onSelectSize={(color) =>
                        handleVariantSwitch("color", color)
                      }
                      itemPadding="px-4 py-2"
                      textSize="text-xs font-bold"
                      title="Colors"
                      renderItemBottom={(color) => {
                        if (color === activeColor) return null;
                        const match = familyProducts.find(
                          (p) =>
                            p.color === color &&
                            (activeSize ? p.size === activeSize : true),
                        );
                        if (
                          match?.price &&
                          match?.salePrice &&
                          Number(match.salePrice) > 0 &&
                          Number(match.salePrice) < Number(match.price)
                        ) {
                          const pct = Math.round(
                            ((Number(match.price) - Number(match.salePrice)) /
                              Number(match.price)) *
                              100,
                          );
                          return (
                            <span className="text-[10px] font-black text-rose-600 tracking-tight bg-rose-50 px-1.5 rounded-md block w-fit mx-auto">
                              {pct}% OFF
                            </span>
                          );
                        }
                        return null;
                      }}
                    />
                  )}

                  {/* Sizes Selector */}
                  {uniqueSizes.length > 0 && (
                    <SizeSelector
                      sizes={uniqueSizes}
                      selectedSize={activeSize}
                      onSelectSize={(size) => handleVariantSwitch("size", size)}
                      itemPadding="px-4 py-2"
                      textSize="text-xs font-bold"
                      title="Size"
                      renderItemBottom={(size) => {
                        if (size === activeSize) return null;
                        const match = familyProducts.find(
                          (p) =>
                            p.size === size &&
                            (activeColor ? p.color === activeColor : true),
                        );
                        if (
                          match?.price &&
                          match?.salePrice &&
                          Number(match.salePrice) > 0 &&
                          Number(match.salePrice) < Number(match.price)
                        ) {
                          const pct = Math.round(
                            ((Number(match.price) - Number(match.salePrice)) /
                              Number(match.price)) *
                              100,
                          );
                          return (
                            <span className="text-[10px] font-black text-rose-600 tracking-tight bg-rose-50 px-1.5 rounded-md block w-fit mx-auto">
                              {pct}% OFF
                            </span>
                          );
                        }
                        return null;
                      }}
                    />
                  )}

                  {/* Weights Selector */}
                  {uniqueWeights.length > 0 && (
                    <SizeSelector
                      sizes={uniqueWeights}
                      selectedSize={activeWeightString}
                      onSelectSize={(weight) =>
                        handleVariantSwitch("weight", weight)
                      }
                      itemPadding="px-4 py-2"
                      textSize="text-xs font-bold "
                      title="Weight"
                      renderItemBottom={(weightStr) => {
                        if (weightStr === activeWeightString) return null;
                        const match = familyProducts.find((p) => {
                          const wStr =
                            p.weight !== null
                              ? `${p.weight} ${p.weightUnit || "kg"}`
                              : "";
                          return wStr === weightStr;
                        });
                        if (
                          match?.price &&
                          match?.salePrice &&
                          Number(match.salePrice) > 0 &&
                          Number(match.salePrice) < Number(match.price)
                        ) {
                          const pct = Math.round(
                            ((Number(match.price) - Number(match.salePrice)) /
                              Number(match.price)) *
                              100,
                          );
                          return (
                            <span className="text-[10px] font-black text-rose-600 tracking-tight bg-rose-50 px-1.5 rounded-md block w-fit mx-auto">
                              {pct}% OFF
                            </span>
                          );
                        }
                        return null;
                      }}
                    />
                  )}
                </div>
              )}

              {/* KEY FEATURES */}
              {normalizedFeatures.length > 0 && (
                <div>
                  <h2 className="text-lg font-semibold text-logoHeading mb-2">
                    Key Features
                  </h2>
                  <ul className="list-disc pl-5 space-y-1 text-sm text-gray-700">
                    {normalizedFeatures.map((item: any) => (
                      <li key={item.id}>{item.title}</li>
                    ))}
                  </ul>
                  <span
                    onClick={scrollToProductInfo}
                    className="block mt-2 text-sm text-blue-500 font-medium cursor-pointer underline"
                  >
                    See more
                  </span>
                </div>
              )}

              {/* SHORT DESCRIPTION DISPLAYED BELOW KEY FEATURES */}
              {shortDescription && (
                <div className="pt-2 text-xs text-gray-600 font-medium leading-relaxed italic border-t border-slate-100">
                  {shortDescription}
                </div>
              )}
            </div>

            {/* CTA PANEL RAIL */}
            <div className="border-l pl-4 flex flex-col h-full">
              {currentFamilyItem &&
                (currentFamilyItem.subscriptionFirstOrderDiscount > 0 ||
                  currentFamilyItem.subscriptionRecurringDiscount > 0) && (
                  <div className="py-2">
                    <SubscribeAndSave
                      originalPrice={displayPrice}
                      discountedPrice={salePrice || displayPrice}
                      firstOrderDiscountPercent={
                        currentFamilyItem.subscriptionFirstOrderDiscount
                      }
                      recurringDiscountPercent={
                        currentFamilyItem.subscriptionRecurringDiscount
                      }
                      onTypeChange={setPurchaseType}
                      onPeriodChange={(cycle: number, interval: number) => {
                        setSelectedBillingCycle(cycle);
                        setSelectedInterval(interval);
                      }}
                    />
                  </div>
                )}

              {/* IN STOCK & OUT OF STOCK */}
              {inStockStatus ? (
                <p className="text-lg text-mainBrandColor pt-1 font-semibold">
                  In Stock
                </p>
              ) : (
                <p className="text-lg text-red-600 pt-1 font-semibold">
                  Sold Out
                </p>
              )}

              <div className="flex flex-col w-full gap-3 pt-3">
                <CartButton
                  title="Add to Cart"
                  buttonclass="w-full"
                  qty={qty}
                  onIncrement={handleIncrement}
                  onDecrement={handleDecrement}
                  onAdd={handleAdd}
                  variant="full"
                  buttonInnerclass="w-full !rounded-full"
                />
                <CommonButton
                  text="Add To Cart"
                  variant="filled"
                  rounded="full"
                  className="w-full"
                  onClick={handleAdd}
                  disabled={!inStockStatus}
                />
                <CommonButton
                  text="Quick Shop"
                  variant="outline"
                  rounded="full"
                  className="w-full"
                  onClick={handleCheckoutClick}
                  disabled={!inStockStatus}
                />
              </div>
              {fetchedProduct?.banner && (
                <div className="flex-1 overflow-hidden rounded-xl py-3 hidden sm:block">
                  <img
                    src={fetchedProduct.banner}
                    alt="Product Banner"
                    className="w-full h-full object-cover rounded-xl"
                  />
                </div>
              )}
            </div>
          </div>

          <CartDrawer
            cart={cart}
            bundles={cartBundles}
            isOpen={isOpen}
            onClose={() => setIsOpen(false)}
            onIncrement={handleCartIncrement}
            onDecrement={handleCartDecrement}
            onRemove={handleCartRemove}
            onAddToCart={handleAddToCartFromSlider}
            handleCheckoutClick={handleCheckoutClick}
          />
        </div>
      </div>

      {/* ACCORDIONS DETAILS TAB */}
      <div ref={productInfoRef} className="py-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 items-stretch">
          <div className="flex flex-col">
            <div className="pb-2">
              <span className="text-xl md:text-2xl font-bold text-iconColor">
                Product Information
              </span>
            </div>
            <div className="flex flex-col flex-1">
              {dynamicSections.map((section, index) => (
                <AccordionItem
                  key={section.key}
                  section={section}
                  isOpen={openIndex === index}
                  onClick={() => handleAccordionClick(index)}
                />
              ))}
            </div>
          </div>
          <div className="h-[200px]">
            <img
              src="https://img.freepik.com/free-vector/hand-drawn-pet-grooming-facebook-cover_23-2150051250.jpg?semt=ais_hybrid&w=740&q=80"
              alt="Grooming Banner"
              className="w-full h-full object-cover rounded-xl"
            />
          </div>
        </div>
      </div>

      {fetchedProduct?.bundle && fetchedProduct.bundle && (
        <div className="w-full pt-4">
          <FrequentlyBought
            bundleData={fetchedProduct.bundle}
            onAddBundle={handleAddBundleToCart}
          />
        </div>
      )}

      {/* DETAILED LONG DESCRIPTION & SPECIFICATIONS AFTER RECOMMENDATIONS */}
      <div className="mt-4 space-y-5">
        {/* INGREDIENTS TEXT */}
        {ingredientsSpec?.value && (
          <div className="space-y-1.5 ">
            <h4 className="text-xs font-bold text-slate-800 uppercase tracking-wider">
              Product Ingredients
            </h4>
            <p className="text-xs text-gray-600 leading-normal">
              {ingredientsSpec.value}
            </p>
          </div>
        )}

        {/* ---------------------------------------------------------
    USAGE GUIDELINES & SAFETY PRECAUTIONS
---------------------------------------------------------- */}
        {instructionsSpec?.value && (
          <div>
            <div className="rounded-2xl border border-slate-200 bg-white overflow-hidden shadow-sm">
              {/* Header */}
              <div className="flex items-center gap-3 px-5 py-4 bg-slate-50 border-b border-slate-200">
                <div className="flex items-center justify-center w-9 h-9 rounded-xl bg-mainBrandColor/10 shrink-0">
                  <ShieldCheck size={19} className="text-mainBrandColor" />
                </div>

                <div>
                  <h3 className="text-sm md:text-base font-bold text-logoHeading">
                    Usage Guidelines & Safety Precautions
                  </h3>

                  <p className="text-xs text-slate-500 mt-0.5">
                    Recommended usage information for this product
                  </p>
                </div>
              </div>

              {/* Instructions Content */}
              <div className="px-5 py-5">
                <div className="space-y-2.5">
                  {String(instructionsSpec.value)
                    .split(/\r?\n/)
                    .map((line: string) => line.trim())
                    .filter(Boolean)
                    .map((line: string, index: number) => {
                      const isHeading =
                        index === 0 ||
                        /daily feeding recommendations/i.test(line) ||
                        /age\s*\(months\)/i.test(line);

                      return isHeading ? (
                        <div
                          key={index}
                          className="pt-1 pb-1 text-sm font-semibold text-logoHeading"
                        >
                          {line}
                        </div>
                      ) : (
                        <div
                          key={index}
                          className="flex items-start gap-3 rounded-lg px-3 py-2.5 bg-slate-50/60 border border-slate-100"
                        >
                          <span className="mt-2 w-1.5 h-1.5 rounded-full bg-mainBrandColor shrink-0" />

                          <span className="text-sm text-slate-600 leading-6">
                            {line}
                          </span>
                        </div>
                      );
                    })}
                </div>

                {/* Safety note */}
                <div className="mt-5 rounded-xl bg-amber-50 border border-amber-100 px-4 py-3">
                  <p className="text-xs sm:text-sm text-amber-800 leading-5">
                    Please follow the recommended feeding quantities and product
                    instructions carefully.
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* MARKETING TEXT BLOCKS */}
        {marketingSpecs.length > 0 && (
          <div className="space-y-2 border-t border-slate-200/80 pt-4">
            <h4 className="text-xs font-bold text-slate-800 uppercase tracking-wider">
              Highlights & Offers
            </h4>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {marketingSpecs.map((mSpec: any, idx: number) => (
                <div
                  key={idx}
                  className="bg-white p-3 rounded-xl border border-slate-200/60 shadow-2xs"
                >
                  <p className="text-xs text-gray-600">{mSpec.value}</p>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* SPECIFICATIONS & FEATURE TAGS */}
        {/* =========================================================
    PRODUCT FEATURES
========================================================= */}
        {categorizedTags.length > 0 && (
          <div className="rounded-2xl border border-slate-200 bg-white overflow-hidden shadow-sm">
            {/* Header */}
            <div className="px-5 py-4 bg-slate-50 border-b border-slate-200">
              <h3 className="text-sm md:text-base font-bold text-logoHeading">
                Product Features
              </h3>

              <p className="text-xs text-slate-500 mt-0.5">
                Key features and benefits of this product
              </p>
            </div>

            {/* Categories */}
            <div className="p-5 space-y-6">
              {categorizedTags.map((group, groupIdx) => (
                <div key={groupIdx}>
                  {/* Category heading */}
                  <div className="flex items-center gap-2 mb-3">
                    <span className="w-1.5 h-4 rounded-full bg-mainBrandColor" />

                    <h4 className="text-xs sm:text-sm font-bold text-slate-700">
                      {group.category}
                    </h4>
                  </div>

                  {/* Feature pills */}
                  <div className="flex flex-wrap gap-2">
                    {group.values.map((value, valueIdx) => (
                      <span
                        key={`${groupIdx}-${valueIdx}`}
                        className="
                  inline-flex
                  items-center
                  rounded-lg
                  border
                  border-slate-200
                  bg-slate-50
                  px-3
                  py-1.5
                  text-xs
                  font-medium
                  text-slate-700
                  transition-all
                  duration-200
                  hover:border-mainBrandColor/30
                  hover:bg-mainBrandColor/5
                "
                      >
                        {value}
                      </span>
                    ))}
                  </div>

                  {/* Category divider */}
                  {groupIdx < categorizedTags.length - 1 && (
                    <div className="mt-6 border-b border-slate-100" />
                  )}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* REVIEWS */}
      <div className="py-4">
        {fetchedProduct &&
        fetchedProduct.reviews &&
        fetchedProduct.reviews.length > 0 ? (
          <ReviewSection reviews={fetchedProduct.reviews} />
        ) : (
          <div className="text-center py-10 border rounded-lg bg-gray-50">
            <p className="text-gray-500 text-base font-medium">
              No Reviews Available For This Option Yet
            </p>
          </div>
        )}
      </div>

      {/* RECOMMENDATIONS / SPONSORED / RELATED PRODUCTS */}
      {relatedProducts.length > 0 && (
        <div className="py-4 bg-white">
          <YouMayLikeProductSection
            showLogo={false}
            title="You may also like"
            productData={relatedProducts}
            handleProductPage={(clickedProductId: number) => {
              navigate(
                `/productpage/customerneproductpage/${clickedProductId}`,
              );
              window.scrollTo({ top: 0, behavior: "smooth" });
            }}
          />
        </div>
      )}

      {/* STICKY BAR QUICK SHEET ACTION SHUTTER ROW */}
      {showStickyBar && (
        <div className="hidden fixed bottom-0 left-0 w-full bg-white shadow-lg border-t z-50 px-4 py-3 lg:flex lg:items-center lg:justify-between animate-slideUp">
          <div className="flex items-center gap-5">
            <div className="w-12 h-12">
              <img
                src={mainImage}
                className="w-full h-full object-cover rounded"
                alt={productName}
              />
            </div>
            <div className="flex flex-col">
              <h1 className="text-base font-bold text-logoHeading">
                {productName}
              </h1>
              <div className="flex items-center gap-2">
                <span className="text-lg font-bold text-mainBrandColor">
                  {salePrice || displayPrice}
                </span>
                {salePrice && (
                  <span className="text-sm text-gray-400 line-through">
                    {displayPrice}
                  </span>
                )}
              </div>
            </div>
          </div>
          <div className="flex gap-5 items-center">
            <div className="w-[100px]">
              <QuantityStepper
                qty={qty}
                onIncrement={handleIncrement}
                onDecrement={handleDecrement}
              />
            </div>
            <CommonButton
              text="Add To Cart"
              variant="filled"
              rounded="md"
              className="px-4 whitespace-nowrap"
              onClick={handleAdd}
              disabled={!inStockStatus}
            />
            <CommonButton
              text="Quick Shop"
              variant="outline"
              rounded="md"
              className="px-4 whitespace-nowrap"
              onClick={handleCheckoutClick}
              disabled={!inStockStatus}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default CustomerNewProductDetailsPage;
